/**
 * Fetch client for the endpoints in docs/specs/app.md's "Endpoints" table. Same-origin fetch
 * carries the login cookie automatically — this module never touches credentials (the auth
 * endpoints themselves are in auth/authApi.ts). Every mutating request sends
 * `x-market-analysis: 1` per the cross-origin hardening note in the spec.
 *
 * Every request goes through `apiFetch`. A 401 from any of them means the login has ended, so it
 * hands off to the auth state holder, which sends the person to `#/login` and remembers where they
 * were (docs/specs/accounts.md, section 8). The ApiError still throws, so the calling view stops.
 */
import type { Artifact, ChatStreamEvent, RunDetail, SessionDetail, SessionSummary } from './types.ts';
import { handleUnauthorized } from './lib/auth.ts';
import { ApiError, MUTATING_HEADER } from './lib/http.ts';

export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status === 401) handleUnauthorized();
  return res;
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as T;
}

export async function createSession(): Promise<{ session_id: string }> {
  const res = await apiFetch('/api/sessions', { method: 'POST', headers: MUTATING_HEADER });
  return asJson(res);
}

export async function listSessions(): Promise<SessionSummary[]> {
  const res = await apiFetch('/api/sessions');
  return asJson(res);
}

export async function getSession(id: string): Promise<SessionDetail> {
  const res = await apiFetch(`/api/sessions/${encodeURIComponent(id)}`);
  return asJson(res);
}

export async function getRun(id: string): Promise<RunDetail> {
  const res = await apiFetch(`/api/runs/${encodeURIComponent(id)}`);
  return asJson(res);
}

/** 404 until the run's artifact is built (spec) — callers distinguish "not ready" from other errors. */
export async function getArtifact(runId: string): Promise<Artifact | null> {
  const res = await apiFetch(`/api/runs/${encodeURIComponent(runId)}/artifact`);
  if (res.status === 404) return null;
  return asJson(res);
}

/**
 * POST one chat turn and stream the SSE response. EventSource can't do POST + custom headers,
 * so this parses `text/event-stream` frames off the fetch body itself. `onEvent` fires once per
 * complete event; the promise resolves after the `done` event (or the stream closing early).
 */
export async function postChat(
  sessionId: string,
  message: string,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}/chat`, {
    method: 'POST',
    headers: { ...MUTATING_HEADER, 'content-type': 'application/json' },
    body: JSON.stringify({ message }),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok || !res.body) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIndex: number;
    // SSE frames are separated by a blank line; \n\n or \r\n\r\n both occur in the wild.
    while ((sepIndex = buffer.search(/\r?\n\r?\n/)) !== -1) {
      const frame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex).replace(/^\r?\n\r?\n/, '');
      const parsed = parseSseFrame(frame);
      if (parsed) onEvent(parsed);
    }
  }
}

function parseSseFrame(frame: string): ChatStreamEvent | null {
  let eventName: string | null = null;
  const dataLines: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('event:')) eventName = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).trim());
  }
  if (!dataLines.length) return null;
  const raw = dataLines.join('\n');
  try {
    const data = raw ? JSON.parse(raw) : {};
    const type = eventName ?? data.type;
    return { type, ...data } as ChatStreamEvent;
  } catch {
    return null;
  }
}

export { ApiError };
